import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { ArrowRight, Code, Megaphone, Zap, Shield, Target, Users, X, Info, Box, ExternalLink, Phone, User, CheckCircle } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

export const Home = () => {
  const [featuredApps, setFeaturedApps] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [modalStep, setModalStep] = useState('info'); // info, form, success
  const [formData, setFormData] = useState({ fullName: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carouselControls = useAnimation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const [appsRes, newsRes, servicesRes] = await Promise.all([
          api.apps.getAll(null, true),
          api.news.getAll(true),
          api.services.getAll()
        ]);
        setFeaturedApps(appsRes.data.slice(0, 3));
        setRecentNews(newsRes.data.slice(0, 2));
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (services.length > 0) {
      carouselControls.start({
        x: ["0%", "-33.33%"],
        transition: { duration: 50, repeat: Infinity, ease: "linear" }
      });
    }
  }, [services, carouselControls]);

  const handleOrderSubmission = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data: user } = await api.auth.getMe();
      if (!user) {
        toast.error('Debes iniciar sesión para solicitar un servicio.');
        navigate('/login');
        return;
      }

      await api.orders.create({
        user_id: user.id,
        service_id: selectedService.id,
        full_name: formData.fullName,
        phone: formData.phone,
        status: 'PENDIENTE'
      });

      setModalStep('success');
      toast.success('Solicitud procesada correctamente.');
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Hubo un error al procesar tu solicitud.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const carouselItems = [...services, ...services, ...services];
  
  return (
    <div className="min-h-screen bg-black text-white selection:bg-[#C6FF00] selection:text-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grid-bg">
        <div className="absolute inset-0 mesh-gradient opacity-30"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 text-center pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-flex items-center space-x-2 mb-8 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-[#C6FF00] animate-pulse"></div>
              <span className="text-[10px] font-black tracking-[0.2em] uppercase text-white/70">
                Nexus Phase // Active
              </span>
            </motion.div>
            
            <h1 className="text-5xl sm:text-8xl lg:text-9xl font-black tracking-tighter mb-8 leading-none">
              BUILD<br />
              <span className="text-gradient">BRAINS</span>
            </h1>
            
            <p className="text-lg sm:text-2xl text-white/40 mb-12 max-w-2xl mx-auto font-medium tracking-tight uppercase px-4">
              Arquitectando el futuro digital con precisión milimétrica y propósito infinito.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <Link
                to="/apps"
                className="w-full sm:w-auto px-10 py-5 rounded-full bg-white text-black font-black text-[10px] uppercase tracking-widest overflow-hidden transition-all duration-500 hover:scale-105"
              >
                <span className="relative z-10 italic">Ver Explorador</span>
              </Link>
              <Link
                to="/planes"
                className="w-full sm:w-auto px-10 py-5 rounded-full border border-white/10 text-white font-black text-[10px] uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-500 italic"
              >
                Protocolos
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Carousel */}
      {services.length > 0 && (
        <section className="py-20 border-b border-white/5 bg-black/40 overflow-hidden relative touch-pan-y">
          <div className="absolute top-0 left-0 w-24 h-full bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
          <div className="absolute top-0 right-0 w-24 h-full bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
          
          <div className="mb-12 px-6 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between">
             <div className="flex items-center gap-4">
                <div className="w-1 h-6 bg-[#C6FF00] rounded-full shadow-[0_0_15px_#C6FF00]"></div>
                <h2 className="text-[10px] font-black uppercase tracking-[0.4em] italic leading-none">Servicios</h2>
             </div>
             <span className="text-[8px] font-black uppercase tracking-widest text-white/20 italic hidden sm:block">Channel_Alpha</span>
          </div>

          <div className="relative group/carousel overflow-visible select-none">
            <motion.div 
              animate={{ x: [0, -(services.length * (window.innerWidth < 640 ? 324 : 374))] }}
              transition={{ 
                duration: services.length * 3, // Faster speed (3s per item)
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="flex gap-6 px-4 w-max"
              style={{ touchAction: 'auto' }}
            >
              {carouselItems.map((service, i) => (
                <button
                  key={`${service.id}-${i}`}
                  onClick={() => { setSelectedService(service); setModalStep('info'); }}
                  className="w-[300px] sm:w-[350px] shrink-0 group relative p-8 sm:p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-[#C6FF00]/40 transition-all duration-700 text-left overflow-hidden flex flex-col justify-between aspect-[16/10] shadow-xl"
                >
                  <div className="absolute top-0 right-0 p-8 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-1000 z-0">
                     <Zap size={80} className="text-[#C6FF00]" />
                  </div>
                  
                  <div className="relative z-10 flex flex-col items-start h-full justify-center">
                    {service.image_url ? (
                      <div className={`overflow-hidden transition-all duration-700 ${service.title ? 'w-12 h-12 mb-4 rounded-xl' : 'w-full h-24 mb-2 flex items-center justify-center'}`}>
                        <img 
                          src={service.image_url} 
                          alt="" 
                          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform pointer-events-none" 
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 mb-6 text-[#C6FF00]">
                         <Box size={20} />
                      </div>
                    )}
                    
                    {service.title && (
                      <h3 className="text-xl sm:text-2xl font-black uppercase italic tracking-tight group-hover:text-[#C6FF00] transition-colors leading-none">
                        {service.title}
                      </h3>
                    )}
                  </div>

                  <div className="relative z-10 flex items-center justify-between text-[7px] font-black uppercase tracking-[0.3em] text-white/20 group-hover:text-[#C6FF00]/60 transition-colors pt-6 border-t border-white/5">
                     <span className="italic">Solicitar Nodo</span>
                     <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              ))}
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Vision Section */}
      <section className="py-20 relative overflow-hidden border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32 mb-12 lg:mb-0">
              <span className="text-[#C6FF00] font-black tracking-widest uppercase text-[8px] mb-4 block italic">Capacidades</span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-6 italic uppercase leading-none">NUESTRA<br/>VISIÓN</h2>
              <p className="text-white/40 text-[9px] sm:text-[10px] leading-relaxed font-black uppercase tracking-widest">
                Desafiamos los límites de lo posible, fusionando ingeniería avanzada con un diseño visionario.
              </p>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              <motion.div
                whileHover={{ y: -5 }}
                className="group p-8 sm:p-10 glass-card rounded-[2rem] border border-white/5 hover:border-[#C6FF00]/20 transition-all duration-500 shadow-xl"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-8 border border-white/10 group-hover:bg-[#C6FF00] group-hover:text-black transition-all">
                  <Code className="w-5 h-5" />
                </div>
                <h3 className="text-lg sm:text-xl font-black mb-4 tracking-tight italic uppercase">Software Avanzado</h3>
                <p className="text-white/30 leading-relaxed mb-8 font-medium text-[9px] uppercase tracking-widest">
                  Sistemas robustos que escalan con tus ambiciones. Web de alto rendimiento y arquitecturas de datos.
                </p>
                <ul className="space-y-3">
                  {['SaaS Personalizado', 'Web3 Systems', 'AI Integration'].map((item) => (
                    <li key={item} className="flex items-center space-x-3 text-[9px] font-black tracking-widest uppercase text-white/50">
                      <div className="w-1 h-1 rounded-full bg-[#C6FF00]"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                whileHover={{ y: -5 }}
                className="group p-8 sm:p-10 glass-card rounded-[2rem] border border-white/5 hover:border-[#C6FF00]/20 transition-all duration-500 shadow-xl"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-8 border border-white/10 group-hover:bg-[#C6FF00] group-hover:text-black transition-all">
                  <Megaphone className="w-5 h-5" />
                </div>
                <h3 className="text-lg sm:text-xl font-black mb-4 tracking-tight italic uppercase">Estrategia</h3>
                <p className="text-white/30 leading-relaxed mb-8 font-medium text-[9px] uppercase tracking-widest">
                  Ingeniería de crecimiento mediante datos y excelencia creativa que captura la atención global.
                </p>
                <ul className="space-y-3">
                  {['Analítica Real', 'Presencia Digital', 'Growth Engineering'].map((item) => (
                    <li key={item} className="flex items-center space-x-3 text-[9px] font-black tracking-widest uppercase text-white/50">
                      <div className="w-1 h-1 rounded-full bg-[#C6FF00]"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Projects */}
      {featuredApps.length > 0 && (
        <section className="py-20 bg-[#030303]">
          <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <div className="max-w-2xl">
                <span className="text-[#C6FF00] font-black tracking-widest uppercase text-[8px] mb-4 block italic leading-none">Proyectos_Live</span>
                <h2 className="text-4xl sm:text-5xl font-black tracking-tighter italic uppercase leading-none">NEXUS STORE</h2>
              </div>
              <Link
                to="/apps"
                className="group inline-flex items-center space-x-3 text-[9px] text-white font-black tracking-widest uppercase hover:text-[#C6FF00] transition-colors italic"
              >
                <span>Acceder Catálogo</span>
                <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
              {featuredApps.map((app, index) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, scale: 0.98 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="group relative"
                >
                  <div className="aspect-[4/5] bg-[#0A0A0A] rounded-[2rem] overflow-hidden border border-white/5 group-hover:border-[#C6FF00]/40 transition-all duration-700 shadow-xl">
                    {app.image_url ? (
                      <img
                        src={app.image_url}
                        alt={app.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 grayscale opacity-40 group-hover:opacity-100"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center mesh-gradient opacity-20">
                        <Zap className="w-12 h-12 text-white/10" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90" />
                    
                    <div className="absolute inset-x-0 bottom-0 p-8 translate-y-2 group-hover:translate-y-0 transition-all duration-700">
                      <div className="text-[#C6FF00] text-[7px] font-black uppercase tracking-[0.3em] mb-3 italic">
                        {app.category}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-black tracking-tighter text-white mb-6 italic uppercase leading-none">{app.name}</h3>
                      <Link
                        to={`/apps/${app.id}`}
                        className="w-full py-4 rounded-xl bg-[#C6FF00] text-black font-black text-[9px] uppercase tracking-widest flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-500"
                      >
                        <span className="italic">Acceso Nodo</span>
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Latest Updates */}
      {recentNews.length > 0 && (
        <section className="py-20 bg-black border-t border-white/5 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-row justify-between items-center mb-16">
              <div className="flex items-center gap-4">
                <div className="w-1 h-6 bg-white/20 rounded-full"></div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tighter italic uppercase">Comms</h2>
              </div>
              <Link
                to="/noticias"
                className="px-6 py-2 rounded-full border border-white/10 hover:border-[#C6FF00] hover:text-[#C6FF00] transition-all text-[8px] font-black uppercase tracking-widest"
              >
                Archivos
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {recentNews.map((news, index) => (
                <motion.article
                  key={news.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -10 : 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer p-6 rounded-[2rem] bg-white/[0.01] border border-white/5 hover:bg-white/[0.02] transition-colors"
                >
                  <Link to={`/noticias/${news.id}`}>
                    <div className="aspect-[16/9] mb-8 rounded-[1.5rem] overflow-hidden border border-white/5 relative">
                      {news.image_url && (
                        <img
                          src={news.image_url}
                          alt={news.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 grayscale opacity-40 group-hover:opacity-100"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-all" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-4">
                        <span className="px-3 py-1 rounded-full bg-[#C6FF00]/10 text-[6px] text-[#C6FF00] font-black uppercase tracking-widest italic">
                          INTERNAL_COMM
                        </span>
                        <span className="text-white/20 text-[6px] font-black uppercase tracking-widest">
                          {new Date(news.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-black tracking-tighter group-hover:text-[#C6FF00] transition-colors leading-tight mb-4 italic uppercase">
                        {news.title}
                      </h3>
                      <p className="text-white/30 leading-relaxed font-medium uppercase text-[9px] tracking-wide line-clamp-2 italic">
                        {news.summary}
                      </p>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Philosophy Section */}
      <section className="py-20 grid-bg relative">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center mb-20 px-4">
            <span className="text-[#C6FF00] font-black tracking-[0.4em] uppercase text-[8px] mb-4 block italic leading-none">Filosofía // Red</span>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter uppercase italic leading-none">DIFERENTES POR DISEÑO</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { icon: Shield, title: 'Hierro Forjado', desc: 'Sistemas resilientes para tu imperio digital.' },
              { icon: Zap, title: 'Alta Velocidad', desc: 'Despliegues relámpago con estándares élite.' },
              { icon: Users, title: 'Consulado', desc: 'Acceso directo a especialistas visionarios.' }
            ].map((item, i) => (
              <div key={i} className="text-center group border border-white/5 p-8 rounded-[2rem] bg-white/[0.01]">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-8 transition-all duration-700 shadow-lg">
                  <item.icon size={28} className="text-white group-hover:text-[#C6FF00] transition-colors" />
                </div>
                <h3 className="text-lg font-black mb-4 uppercase italic leading-none">{item.title}</h3>
                <p className="text-white/20 leading-relaxed font-medium uppercase text-[8px] tracking-[0.1em] max-w-[220px] mx-auto italic">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Modal - Multi-step Workflow */}
      <AnimatePresence>
        {selectedService && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 text-white font-sans">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedService(null)}
              className="absolute inset-0 bg-black/95 backdrop-blur-2xl" 
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 p-8 sm:p-10 rounded-[2.5rem] relative z-10 overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-[#C6FF00] shadow-[0_0_15px_#C6FF00]" />
              
              <div className="flex items-center justify-between mb-8">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 text-[#C6FF00]">
                       <Box size={22} />
                    </div>
                    <div>
                       <span className="text-[#C6FF00] text-[7px] font-black uppercase tracking-[0.4em] mb-1 block italic leading-none">{modalStep === 'info' ? 'Service_Module' : modalStep === 'form' ? 'User_Registry' : 'Action_Complete'}</span>
                       <h2 className="text-xl sm:text-2xl font-black uppercase italic tracking-tight leading-none">{selectedService.title}</h2>
                    </div>
                 </div>
                 <button onClick={() => setSelectedService(null)} className="p-2 text-white/20 hover:text-white transition-all"><X size={20} /></button>
              </div>

              <div className="space-y-8">
                 {modalStep === 'info' && (
                   <div className="space-y-8">
                      <div className="p-6 sm:p-8 bg-white/[0.02] border border-white/5 rounded-2xl">
                         <p className="text-white/50 text-[11px] sm:text-sm font-medium leading-relaxed italic uppercase tracking-tight">
                            {selectedService.description || 'Documentación técnica bajo procesamiento de sistema.'}
                         </p>
                      </div>
                      <button 
                        onClick={() => setModalStep('form')}
                        className="w-full py-5 bg-[#C6FF00] text-black rounded-xl font-black uppercase tracking-[0.2em] text-[9px] flex items-center justify-center gap-2 hover:scale-[1.02] transition-all"
                      >
                         <Zap size={14} /> Solicitar Nodo
                      </button>
                   </div>
                 )}

                 {modalStep === 'form' && (
                   <form onSubmit={handleOrderSubmission} className="space-y-6">
                      <div className="space-y-4">
                         <div className="relative group">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#C6FF00] transition-colors"><User size={16} /></span>
                            <input 
                              required
                              type="text" 
                              placeholder="NOMBRE COMPLETO" 
                              className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#C6FF00]/40 transition-all"
                              value={formData.fullName}
                              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                            />
                         </div>
                         <div className="relative group">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#C6FF00] transition-colors"><Phone size={16} /></span>
                            <input 
                              required
                              type="tel" 
                              placeholder="NÚMERO DE TELÉFONO" 
                              className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#C6FF00]/40 transition-all"
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            />
                         </div>
                      </div>
                      <div className="flex gap-3">
                         <button 
                           type="button"
                           onClick={() => setModalStep('info')}
                           className="flex-1 py-4 bg-white/5 text-white/40 rounded-xl font-black uppercase tracking-[0.2em] text-[8px] hover:text-white transition-all"
                         >
                            Volver
                         </button>
                         <button 
                           disabled={isSubmitting}
                           type="submit"
                           className="flex-[2] py-4 bg-[#C6FF00] text-black rounded-xl font-black uppercase tracking-[0.2em] text-[9px] flex items-center justify-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
                         >
                            {isSubmitting ? 'Procesando...' : 'Guardar y Continuar'} <ArrowRight size={14} />
                         </button>
                      </div>
                   </form>
                 )}

                 {modalStep === 'success' && (
                   <div className="text-center space-y-8 py-4">
                      <div className="w-20 h-20 bg-[#C6FF00]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                         <CheckCircle size={40} className="text-[#C6FF00]" />
                      </div>
                      <h3 className="text-xl font-black uppercase italic tracking-widest">Solicitud Registrada</h3>
                      <p className="text-white/40 text-[10px] font-black tracking-widest uppercase italic max-w-xs mx-auto">
                        Tu nodo ha sido asignado al carrito. Puedes seguir el flujo del proyecto desde tu panel personal.
                      </p>
                      <button 
                        onClick={() => navigate('/panel')}
                        className="w-full py-5 bg-white text-black rounded-xl font-black uppercase tracking-[0.2em] text-[9px] flex items-center justify-center gap-2 hover:scale-[1.02] transition-all"
                      >
                         Ver mi Panel <ArrowRight size={14} />
                      </button>
                   </div>
                 )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {/* Final CTA */}
      <section className="py-24 bg-black relative overflow-hidden text-center px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] mesh-gradient opacity-10 blur-[100px]"></div>
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-12 sm:p-16 rounded-[2.5rem] border border-white/10 bg-white/[0.01] backdrop-blur-xl relative overflow-hidden group shadow-2xl"
          >
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-8 italic uppercase leading-none">
              ¿LISTO PARA<br/><span className="text-[#C6FF00]">EVOLUCIONAR?</span>
            </h2>
            <Link
              to="/planes"
              className="inline-flex items-center space-x-4 px-10 py-5 rounded-full bg-[#C6FF00] text-black font-black text-[9px] uppercase tracking-[0.3em] transition-all hover:scale-105 italic"
            >
              <span>Establecer Sincronía</span>
              <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
